import { motion } from "framer-motion";
import { useEffect, useState } from "react";

interface ConfettiPiece {
  id: number;
  x: number;
  color: string;
  size: number;
  rotation: number;
  delay: number;
  type: "square" | "circle" | "ribbon";
}

const ValentineConfetti = () => {
  const [pieces, setPieces] = useState<ConfettiPiece[]>([]);

  useEffect(() => {
    const colors = [
      "#ec4899", // pink-500
      "#f43f5e", // rose-500
      "#ef4444", // red-500
      "#f97316", // orange-500
      "#fbbf24", // amber-400
      "#a855f7", // purple-500
      "#ffffff", // white
      "#fb7185", // rose-400
      "#f472b6", // pink-400
      "#c084fc", // purple-400
    ];

    const types: ("square" | "circle" | "ribbon")[] = ["square", "circle", "ribbon"];

    const newPieces: ConfettiPiece[] = Array.from({ length: 200 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      color: colors[Math.floor(Math.random() * colors.length)],
      size: 6 + Math.random() * 14,
      rotation: Math.random() * 360,
      delay: Math.random() * 0.8,
      type: types[Math.floor(Math.random() * types.length)],
    }));

    setPieces(newPieces);
  }, []);

  return (
    <div className="fixed inset-0 pointer-events-none overflow-hidden z-50">
      {pieces.map((piece) => (
        <motion.div
          key={piece.id}
          className="absolute"
          style={{
            left: `${piece.x}%`,
            top: -30,
            width: piece.size,
            height: piece.type === "ribbon" ? piece.size * 2.5 : piece.size * 0.6,
            backgroundColor: piece.color,
            borderRadius: piece.type === "circle" ? "50%" : piece.type === "ribbon" ? "2px" : "2px",
            boxShadow: `0 0 6px ${piece.color}`,
          }}
          initial={{ 
            y: -30, 
            rotate: piece.rotation,
            opacity: 1,
            scale: 1,
          }}
          animate={{
            y: window.innerHeight + 100,
            rotate: piece.rotation + 1080,
            opacity: [1, 1, 1, 0.8, 0],
            x: [0, (Math.random() - 0.5) * 100, (Math.random() - 0.5) * 150],
          }}
          transition={{
            duration: 3.5 + Math.random() * 2.5,
            delay: piece.delay,
            ease: "linear",
          }}
        />
      ))}

      {/* Heart-shaped confetti */}
      {Array.from({ length: 40 }).map((_, i) => (
        <motion.div
          key={`heart-${i}`}
          className="absolute text-2xl"
          style={{
            left: `${Math.random() * 100}%`,
            top: -40,
            filter: "drop-shadow(0 0 8px rgba(236,72,153,0.6))",
          }}
          initial={{ y: -40, opacity: 1, rotate: 0, scale: 0.8 + Math.random() * 0.6 }}
          animate={{
            y: window.innerHeight + 80,
            opacity: [1, 1, 1, 0.5, 0],
            rotate: 360 + Math.random() * 360,
            x: [0, Math.sin(i) * 80, Math.cos(i) * 60, 0],
          }}
          transition={{
            duration: 4.5 + Math.random() * 2,
            delay: Math.random() * 1,
            ease: "linear",
          }}
        >
          {["💖", "💕", "💗", "💓", "❤️", "🩷", "💝"][Math.floor(Math.random() * 7)]}
        </motion.div>
      ))}

      {/* Sparkle burst */}
      {Array.from({ length: 30 }).map((_, i) => (
        <motion.div
          key={`sparkle-${i}`}
          className="absolute text-lg"
          style={{
            left: `${Math.random() * 100}%`,
            top: -20,
          }}
          initial={{ y: -20, opacity: 1, scale: 0.5 }}
          animate={{
            y: window.innerHeight + 50,
            opacity: [1, 1, 0],
            scale: [0.5, 1.2, 0.8],
            x: [0, (Math.random() - 0.5) * 120],
          }}
          transition={{
            duration: 3 + Math.random() * 2,
            delay: Math.random() * 0.5,
            ease: "linear",
          }}
        >
          {["✨", "⭐", "🌟", "💫"][Math.floor(Math.random() * 4)]}
        </motion.div>
      ))}

      {/* Rose petals */}
      {Array.from({ length: 20 }).map((_, i) => (
        <motion.div
          key={`rose-${i}`}
          className="absolute text-xl"
          style={{
            left: `${Math.random() * 100}%`,
            top: -30,
          }}
          initial={{ y: -30, opacity: 1, rotate: Math.random() * 180 }}
          animate={{
            y: window.innerHeight + 60,
            opacity: [1, 1, 0.7, 0],
            rotate: 360,
            x: [0, Math.sin(i * 0.5) * 100, 0, Math.cos(i * 0.5) * 80],
          }}
          transition={{
            duration: 5 + Math.random() * 2,
            delay: Math.random() * 1.2,
            ease: "easeInOut",
          }}
        >
          {["🌹", "🌸", "🌺", "🥀"][Math.floor(Math.random() * 4)]}
        </motion.div>
      ))}
    </div>
  );
};

export default ValentineConfetti;
